import React from 'react';
import { X, Clock, ArrowRight, Tag, CheckCircle2 } from 'lucide-react';
import { useFollowUps } from '../context/FollowUpsContext';

interface SummaryModalProps {
  summary: {
    id: string;
    title: string;
    date: string;
    time: string;
    double: {
      name: string;
    };
    summary: string | null;
    key_points: string[] | null;
    followUps: {
      id: string;
      title: string;
      description: string | null;
      status: 'pending' | 'in_progress' | 'completed';
      assignee: string | null;
      due_date: string | null;
    }[];
  } | null;
  onClose: () => void;
}

const SummaryModal = ({ summary, onClose }: SummaryModalProps) => {
  if (!summary) return null;

  const { followUps, updateFollowUp } = useFollowUps();

  const handleTagClick = (followUpId: number, tag: 'triage' | 'own' | 'delegate' | 'archive') => {
    updateFollowUp(followUpId, { tag });
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white w-full max-w-3xl my-8 rounded-xl shadow-retro border-2 border-dark"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">{summary.title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="flex items-center mb-6 text-sm text-gray-600">
            <div className="flex items-center">
              <Clock className="h-4 w-4" />
              <span>{summary.date} at {summary.time} • {summary.double.name}</span>
            </div>
          </div>

          <div className="space-y-6">
            <section>
              <h3 className="text-lg font-semibold mb-3">Key Points</h3>
              <ul className="space-y-2">
                {(Array.isArray(summary.key_points) ? summary.key_points : []).map((point, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">Meeting Summary</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="whitespace-pre-wrap">{summary.summary}</p>
              </div>
            </section>
            
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Follow-Ups</h3>
                <button 
                  onClick={() => window.location.href = `/follow-ups?summary=${summary.id}`}
                  className="text-primary hover:text-primary/80 text-sm font-medium flex items-center gap-1"
                >
                  View All
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-3">
                {(Array.isArray(summary.followUps) ? summary.followUps : []).map((followUp) => (
                  <label
                    key={followUp.id}
                    className="flex items-center gap-4 p-3 hover:bg-gray-50 rounded-lg group transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={followUp.status === 'completed'}
                      className="h-5 w-5 rounded border-2 border-gray-300 text-primary focus:ring-primary"
                      onChange={() => {}}
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">{followUp.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleTagClick(followUp.id, 'triage')}
                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                          followUps[followUp.id]?.tag === 'triage' 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        }`}
                      >
                        <Tag className="h-3 w-3 inline mr-1" />
                        Triage
                      </button>
                      <button
                        onClick={() => handleTagClick(followUp.id, 'own')}
                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                          followUps[followUp.id]?.tag === 'own'
                            ? 'bg-green-500 text-white'
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        <Tag className="h-3 w-3 inline mr-1" />
                        Own
                      </button>
                      <button
                        onClick={() => handleTagClick(followUp.id, 'delegate')}
                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                          followUps[followUp.id]?.tag === 'delegate'
                            ? 'bg-yellow-500 text-white'
                            : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                        }`}
                      >
                        <Tag className="h-3 w-3 inline mr-1" />
                        Delegate
                      </button>
                      <button
                        onClick={() => handleTagClick(followUp.id, 'archive')}
                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                          followUps[followUp.id]?.tag === 'archive'
                            ? 'bg-red-500 text-white'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                      >
                        <Tag className="h-3 w-3 inline mr-1" />
                        Archive
                      </button>
                    </div>
                  </label>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryModal;